import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { inventoryApi } from '../../api/inventory-api-instance';
import { stockApi } from '../../api/stock-api';
import {
  stockUnitLabels,
  stockUsageLabels,
  stockUsages,
  type StockItem,
} from '../../domain/fulfillment';
import type { Part } from '../../domain/inventory';
import { stockTotals } from '../../domain/stock';
import { FULL_FLEET_ITEMS_QUERY_KEY, FULL_FLEET_PAGE_SIZE } from '../dashboard/aggregations';
import { STOCK_QUERY_KEYS } from '../stock/use-stock-references';
import {
  entryChoice,
  filterLineEntries,
  inStorageByPart,
  lineChoiceKey,
  partTypeEntries,
  type LineChoice,
  type LineEntry,
  type LineFilter,
  type PartTypeEntry,
} from './line-picker-entries';
import { partGenerationLabels } from './part-choice';
import { partTypePhoto } from './part-type-photos';

type Totals = ReturnType<typeof stockTotals>;

interface LineEntries {
  entries: LineEntry[];
  totals: (stockItemId: string) => Totals | undefined;
}

/**
 * Serialized part types and stock items in one list, with what is in
 * storage. The fleet's items are the ones the app shell already loads.
 */
function useLineEntries(stockItems: readonly StockItem[], parts: readonly Part[]): LineEntries {
  const balances = useQuery({
    queryKey: STOCK_QUERY_KEYS.balances,
    queryFn: () => stockApi.listStockBalances(),
  });
  const items = useQuery({
    queryKey: FULL_FLEET_ITEMS_QUERY_KEY,
    queryFn: () => inventoryApi.listItems({ pageSize: FULL_FLEET_PAGE_SIZE }),
  });
  const totals = useCallback(
    (id: string): Totals | undefined =>
      balances.data ? stockTotals(balances.data, id) : undefined,
    [balances.data],
  );
  const entries = useMemo(() => {
    const inStorage = items.data ? inStorageByPart(items.data.data) : undefined;
    return [
      ...partTypeEntries(parts, inStorage),
      ...stockItems.map((item): LineEntry => ({ kind: 'stock', key: `stock:${item.id}`, item })),
    ];
  }, [items.data, parts, stockItems]);
  return { entries, totals };
}

/**
 * Chooses what an order line is for (tidaltech/koi#8465): a serialized part
 * type or a stock item, from one wide list that shows each one's photo, full
 * name and what is available. Whoever makes the order needn't know which
 * kind of line an item takes.
 */
export function LineItemPicker({
  ariaLabel,
  value,
  stockItems,
  parts,
  onChange,
}: {
  ariaLabel: string;
  value: LineChoice | undefined;
  stockItems: readonly StockItem[];
  parts: readonly Part[];
  onChange: (choice: LineChoice) => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const lineEntries = useLineEntries(stockItems, parts);
  const selected = value
    ? lineEntries.entries.find((entry) => entry.key === lineChoiceKey(value))
    : undefined;

  function close(): void {
    setOpen(false);
    triggerRef.current?.focus();
  }

  return (
    <>
      {selected ? (
        <div className="stock-picker-chosen">
          <EntryPhoto entry={selected} className="stock-card-photo" />
          <div className="stock-picker-chosen-text">
            <EntrySummary entry={selected} totals={lineEntries.totals} />
          </div>
          <button
            ref={triggerRef}
            type="button"
            className="link-button"
            onClick={() => setOpen(true)}
            aria-label={`Change ${ariaLabel}`}
          >
            Change
          </button>
        </div>
      ) : (
        <button
          ref={triggerRef}
          type="button"
          className="stock-picker-trigger"
          onClick={() => setOpen(true)}
          aria-label={ariaLabel}
        >
          Choose an item…
        </button>
      )}
      {open
        ? createPortal(
            <LineItemPickerDialog
              title={ariaLabel}
              lineEntries={lineEntries}
              parts={parts}
              initialKey={selected?.key ?? ''}
              onChoose={(choice) => {
                onChange(choice);
                close();
              }}
              onClose={close}
            />,
            document.body,
          )
        : null}
    </>
  );
}

/** The list on its own, for adding a line: nothing is added until something is chosen. */
export function AddLinePicker({
  title,
  stockItems,
  parts,
  onChoose,
  onClose,
}: {
  title: string;
  stockItems: readonly StockItem[];
  parts: readonly Part[];
  onChoose: (choice: LineChoice) => void;
  onClose: () => void;
}) {
  const lineEntries = useLineEntries(stockItems, parts);
  return createPortal(
    <LineItemPickerDialog
      title={title}
      lineEntries={lineEntries}
      parts={parts}
      initialKey=""
      onChoose={onChoose}
      onClose={onClose}
    />,
    document.body,
  );
}

function LineItemPickerDialog({
  title,
  lineEntries,
  parts,
  initialKey,
  onChoose,
  onClose,
}: {
  title: string;
  lineEntries: LineEntries;
  parts: readonly Part[];
  initialKey: string;
  onChoose: (choice: LineChoice) => void;
  onClose: () => void;
}) {
  const { entries, totals } = lineEntries;
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<LineFilter>('');
  const [availableOnly, setAvailableOnly] = useState(false);
  const [highlightedKey, setHighlightedKey] = useState(initialKey);
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);

  const matches = useMemo(
    () =>
      filterLineEntries(entries, {
        filter,
        availableOnly,
        query,
        parts,
        stockAvailable: (id) => totals(id)?.available,
      }),
    [entries, filter, availableOnly, query, parts, totals],
  );
  // Keep a highlight on screen so the preview always shows something.
  const highlighted = matches.find((entry) => entry.key === highlightedKey) ?? matches[0];
  const optionId = (entry: LineEntry) => `${listId}-${entry.key.replace(/[^\w-]/g, '_')}`;

  useEffect(() => {
    if (!highlighted) return;
    const row = listRef.current?.querySelector<HTMLElement>(
      `[data-key="${CSS.escape(highlighted.key)}"]`,
    );
    row?.scrollIntoView({ block: 'nearest' });
  }, [highlighted]);

  function move(step: number): void {
    if (matches.length === 0) return;
    const index = highlighted ? matches.indexOf(highlighted) : -1;
    const next = matches[Math.min(matches.length - 1, Math.max(0, index + step))];
    if (next) setHighlightedKey(next.key);
  }

  return (
    <div
      className="drawer-overlay centered-overlay stock-picker-overlay"
      role="presentation"
      onClick={(event) => {
        event.stopPropagation();
        onClose();
      }}
    >
      <div
        className="confirmation-dialog stock-picker-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            // The order drawer underneath must stay open.
            event.stopPropagation();
            onClose();
          }
        }}
      >
        <div className="drawer-heading">
          <h2>Choose an item</h2>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close item list"
          >
            ×
          </button>
        </div>
        <div className="stock-picker-filters">
          <input
            type="search"
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                move(event.key === 'ArrowDown' ? 1 : -1);
              } else if (event.key === 'Enter') {
                event.preventDefault();
                if (highlighted) onChoose(entryChoice(highlighted));
              }
            }}
            placeholder="Search name, SKU, barcode, build or description"
            aria-label="Search items"
            aria-controls={listId}
            aria-activedescendant={highlighted ? optionId(highlighted) : undefined}
          />
          <select
            value={filter}
            onChange={(event) => setFilter(event.target.value as LineFilter)}
            aria-label="Kind of item"
          >
            <option value="">All items</option>
            <option value="SERIALIZED">Serialized</option>
            {stockUsages.map((u) => (
              <option key={u} value={u}>
                {stockUsageLabels[u]}
              </option>
            ))}
          </select>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={availableOnly}
              onChange={(event) => setAvailableOnly(event.target.checked)}
            />
            Available only
          </label>
        </div>
        <div className="stock-picker-body">
          {matches.length === 0 ? (
            <p className="empty-note">No items match.</p>
          ) : (
            <ul id={listId} ref={listRef} className="stock-picker-list" role="listbox">
              {matches.map((entry) => {
                const isHighlighted = entry.key === highlighted?.key;
                return (
                  <li
                    key={entry.key}
                    id={optionId(entry)}
                    data-key={entry.key}
                    role="option"
                    aria-selected={isHighlighted}
                    className={`stock-picker-row${isHighlighted ? ' is-highlighted' : ''}`}
                    onClick={() => setHighlightedKey(entry.key)}
                    onDoubleClick={() => onChoose(entryChoice(entry))}
                  >
                    <EntryPhoto entry={entry} className="stock-card-photo" />
                    <div className="stock-picker-row-text">
                      <EntrySummary entry={entry} totals={totals} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {highlighted ? (
            <EntryPreview
              entry={highlighted}
              parts={parts}
              totals={totals}
              onChoose={() => onChoose(entryChoice(highlighted))}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Name, code and what is available, as a row and the chosen line show it. */
function EntrySummary({
  entry,
  totals,
}: {
  entry: LineEntry;
  totals: (id: string) => Totals | undefined;
}) {
  if (entry.kind === 'serialized') {
    return (
      <>
        <strong>{entry.name}</strong>
        <span className="stock-picker-meta">
          <span>Serialized</span>
          <InStorage entry={entry} />
        </span>
      </>
    );
  }
  const { item } = entry;
  return (
    <>
      <strong>{item.displayName}</strong>
      <span className="stock-picker-meta">
        <span className="mono">{item.sku}</span>
        <Availability item={item} totals={totals(item.id)} />
      </span>
      {item.description ? (
        <span className="stock-picker-description">{item.description}</span>
      ) : null}
    </>
  );
}

function EntryPreview({
  entry,
  parts,
  totals,
  onChoose,
}: {
  entry: LineEntry;
  parts: readonly Part[];
  totals: (id: string) => Totals | undefined;
  onChoose: () => void;
}) {
  const name = entry.kind === 'serialized' ? entry.name : entry.item.displayName;
  return (
    <aside className="stock-picker-preview" aria-label={`About ${name}`}>
      <EntryPhoto entry={entry} className="stock-picker-preview-photo" />
      <h3>{name}</h3>
      {entry.kind === 'serialized' ? (
        <PartTypeDetails entry={entry} parts={parts} />
      ) : (
        <StockItemDetails item={entry.item} totals={totals(entry.item.id)} />
      )}
      <button type="button" className="primary" onClick={onChoose}>
        Choose this item
      </button>
    </aside>
  );
}

function PartTypeDetails({ entry, parts }: { entry: PartTypeEntry; parts: readonly Part[] }) {
  const builds = entry.partIds.map((id) => {
    const part = parts.find((candidate) => candidate.id === id);
    return part ? `${part.build} (${id})` : id;
  });
  return (
    <>
      <span className="state-badge">Serialized</span>
      <p className="stock-picker-preview-description">
        Scanned by serial number when packed. Builds: {builds.join(', ')}.
      </p>
      {entry.inStorage !== undefined ? (
        <dl className="stock-figures">
          <div>
            <dt>In storage</dt>
            <dd className={entry.inStorage <= 0 ? 'stock-empty' : undefined}>
              {entry.inStorage} pcs
            </dd>
          </div>
          {entry.byGeneration.map(({ generation, count }) => (
            <div key={generation}>
              <dt>{partGenerationLabels[generation]}</dt>
              <dd>{count ?? 0} pcs</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </>
  );
}

function StockItemDetails({ item, totals }: { item: StockItem; totals: Totals | undefined }) {
  const unit = stockUnitLabels[item.stockUnit];
  return (
    <>
      <span className="mono">{item.sku}</span>
      {item.usage ? <span className="state-badge">{stockUsageLabels[item.usage]}</span> : null}
      <p className="stock-picker-preview-description">
        {item.description || 'No description yet.'}
      </p>
      {totals ? (
        <dl className="stock-figures">
          <div>
            <dt>On hand</dt>
            <dd>
              {totals.onHand} {unit}
            </dd>
          </div>
          <div>
            <dt>Reserved</dt>
            <dd>
              {totals.allocated} {unit}
            </dd>
          </div>
          <div>
            <dt>Available</dt>
            <dd className={totals.available <= 0 ? 'stock-empty' : undefined}>
              {totals.available} {unit}
            </dd>
          </div>
        </dl>
      ) : null}
    </>
  );
}

function InStorage({ entry }: { entry: PartTypeEntry }) {
  if (entry.inStorage === undefined) return null;
  return (
    <span className={entry.inStorage <= 0 ? 'stock-empty' : undefined}>
      {entry.inStorage} in storage
    </span>
  );
}

function Availability({ item, totals }: { item: StockItem; totals: Totals | undefined }) {
  if (!totals) return null;
  return (
    <span className={totals.available <= 0 ? 'stock-empty' : undefined}>
      {totals.available} {stockUnitLabels[item.stockUnit]} available
    </span>
  );
}

/**
 * Every entry keeps the photo slot, so rows line up with or without one. A
 * photo that fails to load shows the empty slot, not a broken image.
 */
function EntryPhoto({ entry, className }: { entry: LineEntry; className: string }) {
  const photo =
    entry.kind === 'serialized'
      ? partTypePhoto(entry.type)
      : stockApi.stockItemImageUrl(entry.item);
  const [failed, setFailed] = useState<string | undefined>();
  return photo && failed !== photo ? (
    <img src={photo} alt="" className={className} loading="lazy" onError={() => setFailed(photo)} />
  ) : (
    <span className={`${className} stock-card-no-photo`} aria-hidden="true" />
  );
}
